import { ApolloError, useQuery } from '@apollo/client';

import {
  GetViewerStripeSubscriptionsDocument,
  GetViewerStripeSubscriptionsQuery,
} from '@sandtech-hub-monorepo/ehub-client';

/**
 * One row of the `MyStripeSubscriptions` result. Named for the query it is cut
 * from: ehub-client already generates `LearnerStripeSubscription` for the
 * schema type, and a second `StripeSubscription` here would read as a rival
 * definition of it.
 */
export type StripeSubscriptionsQueryRow =
  GetViewerStripeSubscriptionsQuery['viewer']['stripeSubscriptions'][number];

/**
 * One shared empty array rather than a fresh literal per render: this value is
 * a dependency of useSubscriptionState's useMemo, which would otherwise miss on
 * every render while the query is in flight.
 */
const NO_SUBSCRIPTIONS: StripeSubscriptionsQueryRow[] = [];

interface UseStripeSubscriptionsResult {
  subscriptions: StripeSubscriptionsQueryRow[];
  loading: boolean;
  error: ApolloError | undefined;
}

/**
 * The caller's card-billed subscriptions, newest first, as the server orders
 * them. Cancelled ones are included — the list is a billing history, not a
 * list of what currently works, and a learner looking for the charge on their
 * statement needs to find the subscription that made it.
 */
export const useStripeSubscriptions = (): UseStripeSubscriptionsResult => {
  const { data, loading, error } = useQuery(
    GetViewerStripeSubscriptionsDocument,
    {
      // Status moves underneath us whenever a webhook lands, so serve the cached
      // answer immediately and correct it from the network.
      fetchPolicy: 'cache-and-network',
    }
  );

  return {
    subscriptions: data?.viewer?.stripeSubscriptions ?? NO_SUBSCRIPTIONS,
    loading: loading && !data,
    error,
  };
};
